import type { Row } from '@tanstack/react-table';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { PortfolioRow } from './types';

// Fixed heights matching Tailwind classes on each item type
const ROW_HEIGHT_PRIMARY = 64; // h-16: group parent, single token, unpriced row
const ROW_HEIGHT_SUBROW = 52; // h-13: sub-rows within expanded groups
const TOGGLE_HEIGHT = 44; // h-11: unpriced-toggle, small-balance-toggle

export type VirtualListItem =
  | { type: 'table-row'; row: Row<PortfolioRow> }
  | { type: 'unpriced-toggle' }
  | { type: 'unpriced-row'; unpricedRow: PortfolioRow; isFirst: boolean }
  | { type: 'small-balance-toggle' };

function getItemHeight(item: VirtualListItem): number {
  switch (item.type) {
    case 'table-row':
      return item.row.depth > 0 ? ROW_HEIGHT_SUBROW : ROW_HEIGHT_PRIMARY;
    case 'unpriced-row':
      return ROW_HEIGHT_PRIMARY;
    case 'unpriced-toggle':
    case 'small-balance-toggle':
      return TOGGLE_HEIGHT;
  }
}

interface UsePortfolioVirtualizerOptions {
  rows: Row<PortfolioRow>[];
  unpricedRows: PortfolioRow[];
  unpricedCount: number;
  showUnpricedTokens: boolean;
  smallBalanceCount: number;
  showSmallBalances: boolean;
  enabled: boolean;
}

export function usePortfolioVirtualizer({
  rows,
  unpricedRows,
  unpricedCount,
  showUnpricedTokens,
  smallBalanceCount,
  showSmallBalances,
  enabled,
}: UsePortfolioVirtualizerOptions) {
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  const items = useMemo<VirtualListItem[]>(() => {
    if (!enabled) return [];

    const result: VirtualListItem[] = [];

    for (const row of rows) {
      result.push({ type: 'table-row', row });
    }

    const showUnpricedSection = unpricedCount > 0 && (smallBalanceCount === 0 || showSmallBalances);
    if (showUnpricedSection) {
      result.push({ type: 'unpriced-toggle' });
      if (showUnpricedTokens) {
        for (const [index, unpricedRow] of unpricedRows.entries()) {
          result.push({ type: 'unpriced-row', unpricedRow, isFirst: index === 0 });
        }
      }
    }

    if (smallBalanceCount > 0) {
      result.push({ type: 'small-balance-toggle' });
    }

    return result;
  }, [
    rows,
    unpricedRows,
    unpricedCount,
    showUnpricedTokens,
    smallBalanceCount,
    showSmallBalances,
    enabled,
  ]);

  // Measure the distance from the top of the document to the list container.
  // useWindowVirtualizer needs this to calculate which items are in the viewport.
  useLayoutEffect(() => {
    if (!listRef.current || !enabled) return;

    const measure = () => {
      const rect = listRef.current?.getBoundingClientRect();
      if (rect) {
        setScrollMargin(rect.top + window.scrollY);
      }
    };

    measure();

    // Re-measure when local or page-level layout changes shift the list's
    // document offset (e.g. async header/chart loading above the table)
    const observer = new ResizeObserver(measure);
    const elementsToObserve = [
      listRef.current.parentElement,
      document.documentElement,
      document.body,
    ].filter((el): el is HTMLElement => el !== null);
    elementsToObserve.forEach((el) => observer.observe(el));
    window.addEventListener('resize', measure);

    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [enabled]);

  const estimateSize = useCallback(
    (index: number) => {
      const item = items[index];
      if (!item) return ROW_HEIGHT_PRIMARY;
      return getItemHeight(item);
    },
    [items],
  );

  const getItemKey = useCallback(
    (index: number) => {
      const item = items[index];
      if (!item) return `virtual-item:${index}`;

      switch (item.type) {
        case 'table-row':
          return `table-row:${item.row.id}`;
        case 'unpriced-row':
          return `unpriced-row:${item.unpricedRow.key}`;
        case 'unpriced-toggle':
          return 'unpriced-toggle';
        case 'small-balance-toggle':
          return 'small-balance-toggle';
      }
    },
    [items],
  );

  const virtualizer = useWindowVirtualizer({
    count: items.length,
    estimateSize,
    getItemKey,
    scrollMargin,
    overscan: 15,
  });

  return {
    listRef,
    virtualizer,
    totalSize: virtualizer.getTotalSize(),
    virtualRows: virtualizer.getVirtualItems(),
    items,
  };
}
